/* ==========================================================================
   Ure po meri — soft seams between sections.

   Where two sections of different colour meet, the edge was a hard cut. This
   blends one into the next across the join:

     · the blend is shared by both sides — the section above fades towards the
       colour halfway between the two, the section below fades out of it — so
       the transition is twice as long and has no edge at the join itself;
     · that halfway colour is mixed in OKLab, so light into navy passes
       through a clean blue-grey instead of a muddy one;
     · the whole join follows one smootherstep curve, so neither end of the
       blend shows a line, and a faint grain breaks up banding on dark grounds.

   It only ever paints over a section's empty top or bottom padding, never over
   its content. Colours are read from the page, so a section recoloured in the
   editor gets a matching seam on its own.
   ========================================================================== */
(function () {
  'use strict';
  var MAX = 300, MIN = 36, DIFF = 18, STOPS = 12;
  var NOISE = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

  function rgba(str) {
    var m = str && str.match(/rgba?\(([^)]+)\)/);
    if (!m) return null;
    var v = m[1].split(/[\s,\/]+/).filter(Boolean).map(parseFloat);
    return { r: v[0], g: v[1], b: v[2], a: v.length > 3 ? v[3] : 1 };
  }
  function hex(s) {
    var m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((s || '').trim());
    if (!m) return rgba(s);
    var h = m[1].length === 3 ? m[1].replace(/./g, '$&$&') : m[1];
    return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16), a: 1 };
  }
  function far(a, b) { return Math.abs(a.r - b.r) + Math.abs(a.g - b.g) + Math.abs(a.b - b.b) > DIFF; }

  // sRGB <-> OKLab, for a perceptually even midpoint
  function lin(c) { c /= 255; return c <= .04045 ? c / 12.92 : Math.pow((c + .055) / 1.055, 2.4); }
  function gam(c) { c = c <= .0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - .055; return Math.max(0, Math.min(255, c * 255)); }
  function toLab(c) {
    var r = lin(c.r), g = lin(c.g), b = lin(c.b);
    var l = Math.cbrt(.4122214708 * r + .5363325363 * g + .0514459929 * b);
    var m = Math.cbrt(.2119034982 * r + .6806995451 * g + .1073969566 * b);
    var s = Math.cbrt(.0883024619 * r + .2817188376 * g + .6299787005 * b);
    return [.2104542553 * l + .7936177850 * m - .0040720468 * s, 1.9779984951 * l - 2.4285922050 * m + .4505937099 * s, .0259040371 * l + .7827717662 * m - .8086757660 * s];
  }
  function fromLab(L) {
    var l = Math.pow(L[0] + .3963377774 * L[1] + .2158037573 * L[2], 3);
    var m = Math.pow(L[0] - .1055613458 * L[1] - .0638541728 * L[2], 3);
    var s = Math.pow(L[0] - .0894841775 * L[1] - 1.2914855480 * L[2], 3);
    return { r: gam(4.0767416621 * l - 3.3077115913 * m + .2309699292 * s), g: gam(-1.2684380046 * l + 2.6097574011 * m - .3413193965 * s), b: gam(-.0041960863 * l - .7034186147 * m + 1.7076147010 * s), a: 1 };
  }
  function mix(a, b, t) {
    var A = toLab(a), B = toLab(b);
    return fromLab([A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t]);
  }
  function css(c, a) { return 'rgba(' + Math.round(c.r) + ',' + Math.round(c.g) + ',' + Math.round(c.b) + ',' + a.toFixed(3) + ')'; }
  function smoother(x) { return x * x * x * (x * (x * 6 - 15) + 10); }

  // A band of one colour whose opacity follows `alpha(x)`, x running from the
  // join (0) to the band's far edge (1).
  function gradient(dir, color, alpha) {
    var out = [];
    for (var i = 0; i <= STOPS; i++) {
      var x = i / STOPS;
      out.push(css(color, Math.max(0, Math.min(1, alpha(x)))) + ' ' + (x * 100).toFixed(1) + '%');
    }
    return 'linear-gradient(' + dir + ',' + out.join(',') + ')';
  }

  // The element that paints a section's ground: the section itself or a
  // full-width child (two levels deep at most) that fills most of it; else
  // the page itself. A section painted with a gradient or picture names its
  // edge colour with data-seam="#rrggbb"; without that it is left alone,
  // because its colour cannot be read.
  function ground(sec) {
    var tagged = sec.matches('[data-seam]') ? sec : sec.querySelector('[data-seam]');
    if (tagged) { var tc = hex(tagged.getAttribute('data-seam')); if (tc) return { el: tagged, c: tc }; }
    var w = sec.offsetWidth, h = sec.offsetHeight, list = [sec];
    [].forEach.call(sec.children, function (c) { list.push(c); [].forEach.call(c.children, function (d) { list.push(d); }); });
    for (var i = 0; i < list.length; i++) {
      var el = list[i];
      if (el.offsetWidth < w * .9 || el.offsetHeight < h * .4) continue;
      var cs = getComputedStyle(el), c = rgba(cs.backgroundColor);
      if (c && c.a > .6) return { el: el, c: c };
      if (cs.backgroundImage !== 'none') return null;
    }
    // a section with no ground of its own shows the page behind it
    var page = rgba(getComputedStyle(document.body).backgroundColor);
    return page && page.a > .6 ? { el: sec, c: page } : null;
  }
  // The first (or last) child that takes up space in the flow: not a script,
  // template or style, not positioned out of the flow, not hidden.
  function edgeChild(el, side) {
    var kids = [].slice.call(el.children);
    if (side !== 'top') kids.reverse();
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      if (/^(SCRIPT|TEMPLATE|STYLE|LINK|META|NOSCRIPT)$/.test(k.tagName) || k.classList.contains('upm-seam')) continue;
      var ks = getComputedStyle(k);
      if (ks.display === 'none' || ks.position === 'absolute' || ks.position === 'fixed') continue;
      return k;
    }
    return null;
  }
  // Empty space at one end of a ground: its own padding, plus its first/last
  // child's when the ground itself has little.
  function room(el, side) {
    var cs = getComputedStyle(el), p = parseFloat(side === 'top' ? cs.paddingTop : cs.paddingBottom) || 0;
    if (p < MIN) {
      var kid = edgeChild(el, side);
      if (kid) {
        var ks = getComputedStyle(kid);
        p += parseFloat(side === 'top' ? ks.paddingTop : ks.paddingBottom) || 0;
      }
    }
    return p;
  }

  function band(host, side, bg, h) {
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    var i = document.createElement('i');
    i.className = 'upm-seam';
    i.setAttribute('aria-hidden', 'true');
    i.style.cssText = 'position:absolute;left:0;right:0;' + side + ':0;height:' + Math.round(h) + 'px;pointer-events:none;z-index:1;display:block;background:' + bg;
    // grain against banding, faded out with the band itself
    var g = document.createElement('i');
    var fade = 'linear-gradient(' + (side === 'top' ? 'to bottom' : 'to top') + ',#000,transparent)';
    g.style.cssText = 'position:absolute;inset:0;display:block;opacity:.05;mix-blend-mode:overlay;background-image:' + NOISE +
      ';-webkit-mask-image:' + fade + ';mask-image:' + fade;
    i.appendChild(g);
    host.appendChild(i);
  }

  function run() {
    [].forEach.call(document.querySelectorAll('.upm-seam'), function (s) { s.remove(); });
    var secs = [].slice.call(document.querySelectorAll('#MainContent > .shopify-section, .shopify-section-group-footer-group'))
      .filter(function (s) { return s.offsetHeight > 0 && getComputedStyle(s).position !== 'fixed'; });
    var grounds = secs.map(ground);
    for (var i = 1; i < secs.length; i++) {
      var up = grounds[i - 1], dn = grounds[i];
      if (!up || !dn || !far(up.c, dn.c)) continue;
      var a = Math.min(MAX, room(up.el, 'bottom')), b = Math.min(MAX, room(dn.el, 'top'));
      if (a >= MIN && b >= MIN) {
        // Shared: the whole join is one smootherstep from the colour above to
        // the colour below, split at its midpoint. Each side lays the midpoint
        // colour over its own ground, so the two halves meet at the same colour
        // and with the same slope: no edge, no plateau.
        var mid = mix(up.c, dn.c, .5);
        band(up.el, 'bottom', gradient('to top', mid, function (x) { return 2 * smoother(.5 - .5 * x); }), a);
        band(dn.el, 'top', gradient('to bottom', mid, function (x) { return 2 * (1 - smoother(.5 + .5 * x)); }), b);
      } else if (b >= MIN) {
        band(dn.el, 'top', gradient('to bottom', up.c, function (x) { return 1 - smoother(x); }), b);
      } else if (a >= MIN) {
        band(up.el, 'bottom', gradient('to top', dn.c, function (x) { return 1 - smoother(x); }), a);
      }
    }
  }

  var t = 0;
  function later() { clearTimeout(t); t = setTimeout(run, 200); }
  if (document.readyState === 'complete') run(); else window.addEventListener('load', run);
  // sections that finish their layout late (app blocks, lazy content)
  setTimeout(later, 2500);
  window.addEventListener('resize', later);
  // the theme editor re-renders sections in place
  document.addEventListener('shopify:section:load', later);
  document.addEventListener('shopify:section:unload', later);
  document.addEventListener('shopify:section:reorder', later);
})();
